/**
 * @tonnie/locale - Type Definitions
 *
 * Core types and constants for the localization system.
 */

// ═══════════════════════════════════════════════════════════════════════════
// SUPPORTED LOCALES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Supported locale codes.
 *
 * `en` is the canonical base (kept as `en` for backward-compat with persisted
 * values). Portuguese ships as two distinct variants — European (pt-PT) and
 * Brazilian (pt-BR) — with separate translation files.
 */
export type SupportedLocale = 'en' | 'pt-PT' | 'pt-BR' | 'es-ES' | 'it-IT';

/**
 * Array of all supported locales
 */
export const SUPPORTED_LOCALES: readonly SupportedLocale[] = [
  'en',
  'pt-PT',
  'pt-BR',
  'es-ES',
  'it-IT',
] as const;

/**
 * Default locale (fallback)
 */
export const DEFAULT_LOCALE: SupportedLocale = 'en';

/**
 * Mapping of locale codes to Intl (BCP-47) locale strings used by the formatters.
 */
export const LOCALE_MAP: Record<SupportedLocale, string> = {
  en: 'en-US',
  'pt-PT': 'pt-PT',
  'pt-BR': 'pt-BR',
  'es-ES': 'es-ES',
  'it-IT': 'it-IT',
};

/**
 * Locale display names (English name + native name for the picker).
 */
export const LOCALE_NAMES: Record<SupportedLocale, { name: string; native: string }> = {
  en: { name: 'English', native: 'English' },
  'pt-PT': { name: 'Portuguese (Portugal)', native: 'Português (Portugal)' },
  'pt-BR': { name: 'Portuguese (Brazil)', native: 'Português (Brasil)' },
  'es-ES': { name: 'Spanish', native: 'Español' },
  'it-IT': { name: 'Italian', native: 'Italiano' },
};

// ═══════════════════════════════════════════════════════════════════════════
// NAMESPACES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Translation namespace identifiers
 */
export type TranslationNamespace =
  // Core
  | 'common'
  | 'nav'
  | 'auth'
  | 'theme'
  | 'notifications'
  | 'errorBoundary'
  // Platform
  | 'dashboard'
  | 'onboarding'
  | 'journeyBuilder'
  | 'profile'
  | 'payment'
  | 'familyProgram'
  // Heroes
  | 'heroes'
  | 'stepper'
  | 'checkup'
  | 'teleportal'
  | 'qultureup'
  | 'care'
  | 'career'
  | 'passport'
  // Features
  | 'copilot'
  | 'cvBuilder'
  | 'assessmentChat'
  | 'feedback'
  | 'sponsorship'
  | 'coupon'
  // Access Control
  | 'persona'
  | 'accessScenario'
  | 'accessControl'
  // Misc
  | 'sponsored'
  | 'deepLink'
  | 'assistantEngine'
  | 'sponsoredAds'
  | 'alt'
  | 'admin'
  | 'notFound';

/**
 * All available namespaces
 */
export const NAMESPACES: readonly TranslationNamespace[] = [
  'common',
  'nav',
  'auth',
  'theme',
  'notifications',
  'errorBoundary',
  'dashboard',
  'onboarding',
  'journeyBuilder',
  'profile',
  'payment',
  'familyProgram',
  'heroes',
  'stepper',
  'checkup',
  'teleportal',
  'qultureup',
  'care',
  'career',
  'passport',
  'copilot',
  'cvBuilder',
  'assessmentChat',
  'feedback',
  'sponsorship',
  'coupon',
  'persona',
  'accessScenario',
  'accessControl',
  'sponsored',
  'deepLink',
  'assistantEngine',
  'sponsoredAds',
  'alt',
  'admin',
  'notFound',
] as const;

/**
 * Default namespace for translations
 */
export const DEFAULT_NAMESPACE: TranslationNamespace = 'common';

// ═══════════════════════════════════════════════════════════════════════════
// LOCALE CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Locale context state
 */
export interface LocaleState {
  /** Current locale */
  locale: SupportedLocale;
  /** Whether the locale is being loaded/changed */
  isLoading: boolean;
  /** Whether the locale system is initialized */
  isInitialized: boolean;
}

/**
 * Locale context actions
 */
export interface LocaleActions {
  /** Change the current locale */
  setLocale: (locale: SupportedLocale) => Promise<void>;
}

/**
 * Full locale context type
 */
export interface LocaleContextType extends LocaleState, LocaleActions {}

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Locale configuration options
 */
export interface LocaleConfig {
  /** Initial locale (defaults to DEFAULT_LOCALE) */
  initialLocale?: SupportedLocale;
  /** Enable browser/device language detection */
  detectLanguage?: boolean;
  /** Enable debug mode */
  debug?: boolean;
}

/**
 * Storage key for persisting locale preference
 */
export const LOCALE_STORAGE_KEY = 'tonnie-locale';

// ═══════════════════════════════════════════════════════════════════════════
// FORMATTER TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Date format options
 */
export type DateFormatStyle = 'short' | 'medium' | 'long' | 'full';

/**
 * Date format options configuration
 */
export interface DateFormatOptions {
  dateStyle?: DateFormatStyle;
  timeStyle?: DateFormatStyle;
}

/**
 * Number format options
 */
export interface NumberFormatOptions {
  style?: 'decimal' | 'currency' | 'percent';
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  notation?: 'standard' | 'compact';
}

/**
 * Supported currency codes
 */
export type CurrencyCode = 'EUR' | 'USD' | 'GBP' | 'BRL';

/**
 * Default currency
 */
export const DEFAULT_CURRENCY: CurrencyCode = 'EUR';
